import { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useDemoNavigate } from '../../hooks/useDemoNavigate';
import { useAppContext } from "../../store/AppContext";
import { useHomeMessages } from '../../hooks/useHomeMessages';
import { useUpgradePrompt } from '../../components/ui/UpgradePromptProvider';
import { useUnitSystem } from "../../hooks/useUnitSystem";
import {
  formatBodyWeight,
  formatMacro,
  formatLiquidVolume,
} from "../../utils/unitConversions";
import { getYesterdayDate, getTodayDate } from "../../utils/date";
import { calculateDailyTDEE } from "../../services/tdee.service";
import { HomeScreenSkeleton } from "../../components/Home/HomeScreenSkeleton";
import { CalorieBalanceCard } from "./CalorieBalanceCard";
import { HomeDashboardTiles } from "./HomeDashboardTiles";
import { useConsistencyStrip } from "../../hooks/useConsistencyStrip";
import type { DailyTDEE } from "../../types/tdee";
import type { ResolvedMessage } from '../../services/messaging/messaging.types';
// Safety Status imports removed - only shows on GLP-1 Hub per Sprint C spec
import { useWeeklyInsight } from '../../hooks/useWeeklyInsight';
import { Balloon } from '../../components/ui/Balloon';
import { useCelebration } from '../../hooks/useCelebration';
import { useGuidance } from '../../hooks/useGuidance';
import { getCelebrations, type CelebrationRecord } from '../../services/celebration.service';
import { getUserProfile } from '../../services/userProfile.service';
import { startPremiumPreview } from '../../services/premiumPreview.service';
import { EmailVerificationBanner } from '../../components/ui/EmailVerificationBanner';
import { PreviewCountdownPill } from '../../components/PreviewCountdownPill';
import { useStarterModal } from '../../hooks/useStarterModal';
import { useA2HSNudge } from '../../hooks/useA2HSNudge';
import { resolveHomeSurfaces } from '../../hooks/useHomeSurfaceCoordinator';
import { StarterModal } from '../../components/ui/StarterModal';

// ── Home message card (Option C — inline module) ──────────────────────────────
// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('home-msg-anim')) {
  const s = document.createElement('style');
  s.id = 'home-msg-anim';
  s.textContent = `@keyframes homeMessageSlideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`;
  document.head.appendChild(s);
}

function HomeMessageCard({ message, onDismiss }: { message: ResolvedMessage; onDismiss: () => void }) {
  const colourMap: Record<string, { bg: string; border: string; bodyColor: string }> = {
    '🔋': { bg: 'rgba(217,119,6,0.07)', border: 'rgba(217,119,6,0.18)', bodyColor: '#92400E' },
    '💪': { bg: 'rgba(0,124,134,0.07)', border: 'rgba(0,124,134,0.18)', bodyColor: '#065F46' },
    '💧': { bg: 'rgba(37,99,235,0.07)', border: 'rgba(37,99,235,0.18)', bodyColor: '#1E3A8A' },
    '🥦': { bg: 'rgba(22,163,74,0.07)', border: 'rgba(22,163,74,0.18)', bodyColor: '#14532D' },
    '🏋️': { bg: 'rgba(124,58,237,0.07)', border: 'rgba(124,58,237,0.18)', bodyColor: '#4C1D95' },
    '📝': { bg: 'rgba(55,65,81,0.06)', border: 'rgba(55,65,81,0.14)', bodyColor: '#374151' },
    '📲': { bg: 'rgba(15,23,42,0.05)', border: 'rgba(15,23,42,0.12)', bodyColor: '#374151' },
    '💉': { bg: 'rgba(0,124,134,0.07)', border: 'rgba(0,124,134,0.18)', bodyColor: '#065F46' },
  };
  const colours = colourMap[message.emoji] ?? { bg: 'rgba(0,0,0,0.04)', border: 'rgba(0,0,0,0.10)', bodyColor: '#374151' };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      background: colours.bg, border: `1px solid ${colours.border}`,
      borderRadius: 14, padding: '10px 12px',
      animation: 'homeMessageSlideIn 220ms ease forwards',
    }}>
      {message.emoji === '🔋' ? (
        <img src="/assets/battery-indicator.png" alt="" style={{ width: 20, height: 20, flexShrink: 0, objectFit: 'contain' }} />
      ) : (
        <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{message.emoji}</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', letterSpacing: -0.2, margin: 0, lineHeight: 1.3 }}>
          {message.headline}
        </p>
        <p style={{ fontSize: 12, color: colours.bodyColor, margin: '2px 0 0', lineHeight: 1.4 }}>
          {message.body}
        </p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        style={{ width: 24, height: 24, borderRadius: '50%', background: 'rgba(0,0,0,0.06)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6B7280', fontSize: 13, flexShrink: 0 }}
        aria-label="Dismiss"
      >✕</button>
    </div>
  );
}

export function HomeScreen() {
  const navigate = useDemoNavigate();
  const location = useLocation();
  const { user, isDemoMode, loading: authLoading, isPremium, previewActive, premiumPreviewUsed, isAdmin, onboardingComplete, emailVerificationCompletedAt } = useAppContext();
  const { isOpen: starterModalOpen, dismiss: dismissStarterModal } = useStarterModal('home', !!onboardingComplete);
  const { isOpen: a2hsNudgeOpen, dismiss: dismissA2hsNudge } = useA2HSNudge(!!onboardingComplete && !isDemoMode);

  const { insight: weeklyInsight } = useWeeklyInsight();

  // ITEM 5: HomeScreen uses both body units (weight) and food units (macros, hydration)
  const { bodyUnits, foodUnits } = useUnitSystem();

  // Shared daily summary state - prevents flicker and recalculation
  const {
    tdee,
    nutrition,
    bodyMetrics,
    dailyBurn,
    targets,
    loading,
    refreshDailySummary,
  } = useAppContext().dailySummary;

  // UI Enhancement 4: Yesterday's calorie balance data
  const [yesterdayTDEE, setYesterdayTDEE] = useState<DailyTDEE | null>(() => {
    try {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const yStr = d.toISOString().slice(0, 10);
      const cached = localStorage.getItem(`home_yesterday_tdee_${yStr}`);
      return cached ? (JSON.parse(cached) as DailyTDEE) : null;
    } catch { return null; }
  });

  const { message: homeMessage, dismiss: dismissHomeMessage } = useHomeMessages(
    isDemoMode ? undefined : user?.uid,
  );

  const dataReady = !loading && !authLoading && !!tdee;
  const { celebration, dismiss: dismissCelebration } = useCelebration(
    isDemoMode ? undefined : user?.uid,
    dataReady,
  );

  const { guidance, dismiss: dismissGuidance } = useGuidance(
    isDemoMode ? undefined : user?.uid,
    dataReady,
    !!celebration,
  );

  // SPRINT A: GLP-1 Dose-Day Nudge — now handled by useHomeMessages

  // C3 — Upgrade prompt: delegated to UpgradePromptProvider
  const { triggerUpgradePrompt } = useUpgradePrompt();

  // Email verification banner is visible when user is unverified, non-admin, non-demo.
  // A2HS must be suppressed while it is showing — both occupy the top of the viewport.
  const emailBannerVisible = !emailVerificationCompletedAt && !isAdmin && !isDemoMode;

  // ── Surface arbitration — only one surface per slot renders at a time ──────
  const { activeModal, activeFloating, activeInline } = resolveHomeSurfaces({
    starterModalOpen,
    celebration,
    guidance,
    a2hsNudgeOpen,
    emailBannerVisible,
    homeMessage,
  });

  // Gold milestone upgrade prompt — effect, not render-time side effect.
  // Fires when a gold celebration becomes the active floating surface
  // and the user is not already premium/preview/demo/admin.
  useEffect(() => {
    if (
      activeFloating === 'celebration' &&
      celebration?.tier === 'gold' &&
      !isPremium &&
      !previewActive &&
      !isDemoMode &&
      !isAdmin
    ) {
      triggerUpgradePrompt('gold_milestone');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFloating, celebration?.tier]);

  const [consistencySheetOpen, setConsistencySheetOpen] = useState(false);
  const [earnedCelebrations, setEarnedCelebrations] = useState<CelebrationRecord[]>([]);

  // C3 — Upgrade prompt: delegated to UpgradePromptProvider
  const [baselineWeight, setBaselineWeight] = useState<number | null>(null);

  // 7-Day Consistency Strip
  // refreshKey increments when meals/workouts are logged to trigger recomputation
  const [consistencyRefreshKey] = useState(0);

  const consistencyData = useConsistencyStrip(
    isDemoMode ? undefined : user?.uid,
    isDemoMode,
    consistencyRefreshKey,
    14,
  );

  // Safety Status removed from Home - only shows on GLP-1 Hub per Sprint C spec

  useEffect(() => {
    // Auto-scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // C3 — Upgrade prompt: fire once when loggingCount >= 5 and user is not premium
  useEffect(() => {
    if (isPremium || previewActive || isDemoMode) return;
    if (!onboardingComplete) return;
    if (consistencyData.loading) return;
    if (consistencyData.loggingCount < 5) return;
    triggerUpgradePrompt('logging_days');
  }, [isPremium, previewActive, isDemoMode, consistencyData.loading, consistencyData.loggingCount]);

  // Trigger 48-hour premium preview after first successful log
  // Fires when: user is not already premium, preview not used, dailySummary has at least 1 entry
  useEffect(() => {
    if (isDemoMode) return;
    if (!user) return;
    if (isPremium && !previewActive) return; // already paid subscriber — skip
    if (premiumPreviewUsed) return; // already used — skip
    if (previewActive) return; // already running — skip
    const hasEntries = (nutrition?.entries?.length ?? 0) > 0 || (dailyBurn?.entries?.length ?? 0) > 0;
    if (!hasEntries) return;
    void startPremiumPreview().catch((err) => {
      console.warn('[HomeScreen] preview start failed (non-critical)', err);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, isDemoMode, premiumPreviewUsed, previewActive]);

  // Safety Status loading removed - only shows on GLP-1 Hub per Sprint C spec

  // BUG FIX A1/A2: Refresh data when navigating back to home
  // This ensures Home shows the same data as Nutrition after edits/deletes
  // Silent refresh to avoid showing loader on navigation
  // ITEM 4: Skip refresh in demo mode (data is static)
  useEffect(() => {
    if (isDemoMode) return; // Demo mode data is static, no need to refresh

    if (location.pathname === "/home" || location.pathname === "/") {
      // Force refresh to ensure we get fresh data from Firestore
      // This prevents ghost calories after edit/delete operations on Nutrition screen
      refreshDailySummary(true, true);
      // NOTE: Consistency strip is NOT refreshed on navigation.
      // It only refreshes when data actually changes (via consistencyRefreshKey).
      // This prevents 14 unnecessary Firestore reads per screen transition.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isDemoMode]);

  // Refresh data when unit system changes to update display values
  // All values are stored in metric internally, only display is converted
  // ITEM 4: Skip refresh in demo mode (data is static)
  useEffect(() => {
    if (user && !isDemoMode) {
      refreshDailySummary(true, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyUnits, foodUnits, isDemoMode, user]);

  // BUG FIX: Offline Logging Reconciliation - Refresh daily summary when sync completes
  // This ensures totals update immediately after offline entries are synced
  useEffect(() => {
    if (!user || isDemoMode) return;

    const handleSyncComplete = async () => {
      console.log("[Home] Offline sync complete event received");

      // Wait a moment for Firestore writes to be visible, then refresh
      setTimeout(async () => {
        try {
          await refreshDailySummary(true, true); // Force refresh, silent to avoid flicker
        } catch (error) {
          console.error(
            "[Home] Failed to refresh daily summary after sync:",
            error,
          );
        }
      }, 1000);
    };

    const handleDailySummaryRefresh = async () => {
      // Refresh silently to avoid flicker
      await refreshDailySummary(true, true);
    };

    window.addEventListener("offline-sync-complete", handleSyncComplete);
    window.addEventListener("daily-summary-refresh", handleDailySummaryRefresh);

    return () => {
      window.removeEventListener("offline-sync-complete", handleSyncComplete);
      window.removeEventListener(
        "daily-summary-refresh",
        handleDailySummaryRefresh,
      );
    };
  }, [user, isDemoMode, refreshDailySummary]);

  // Verification logging for TDEE calculations
  // This helps verify that resting TDEE and exercise calculations are feeding correctly
  // Must be placed before any early returns to follow Rules of Hooks

  // CRITICAL FIX: If TDEE is null but we have a user, we should still be loading or there's an error
  // Don't show zeros - this indicates a problem. Force a refresh if TDEE is null and we're not loading.
  // Must be placed before any early returns to follow Rules of Hooks
  // ITEM 4: Skip in demo mode (demo data is always available)
  useEffect(() => {
    if (isDemoMode) return; // Demo mode always has data
    if (user && !loading && !tdee) {
      // TDEE should always exist if user has completed onboarding
      // If it's null, try to refresh it
      console.warn("[HomeScreen] TDEE is null - attempting to refresh");
      refreshDailySummary(true, false);
    }
  }, [user, loading, tdee, refreshDailySummary, isDemoMode]);

  // ITEM 4: Log demo mode data once (not on every render)
  // Must be placed before any early returns to follow Rules of Hooks
  useEffect(() => {
    if (isDemoMode && nutrition && tdee) {
    }
  }, [isDemoMode, nutrition, tdee, dailyBurn]);

  // UI Enhancement 4: Load yesterday's calorie balance data
  useEffect(() => {
    if (!user && !isDemoMode) return;

    async function loadYesterdayData() {
      try {
        if (!user && !isDemoMode) return;
    const uid = isDemoMode ? 'demo-user' : user!.uid;
        const yesterdayDate = getYesterdayDate();
        const yesterdayData = await calculateDailyTDEE(
          uid,
          yesterdayDate,
        );
        // Only update state and cache if value changed — avoids re-render on navigation
        setYesterdayTDEE(prev => {
          if (prev?.deficit === yesterdayData?.deficit) return prev;
          return yesterdayData;
        });
        try {
          const d = new Date();
          d.setDate(d.getDate() - 1);
          const yStr = d.toISOString().slice(0, 10);
          localStorage.setItem(`home_yesterday_tdee_${yStr}`, JSON.stringify(yesterdayData));
        } catch { /* storage unavailable */ }
      } catch (error) {
        console.error("[HomeScreen] Failed to load yesterday's TDEE:", error);
        setYesterdayTDEE(null);
      }
    }

    loadYesterdayData();
  }, [user, isDemoMode]);

  // BUG FIX C2: CRITICAL - Hydration must be in ml (NOT L) for both display AND target comparison
  // nutrition.totalHydration is in ml
  // targets.hydration.min/max is in ml (e.g., 2800ml for 80kg person)
  // formatLiquidVolume() expects ml as input
  // The original / 1000 conversion was WRONG - it caused:
  // 1. "450ml" to display as "0" (formatLiquidVolume(0.45) rounds to 0)
  // 2. Target comparison to fail (0.45 < 2800 always fails)
  const nutritionData = nutrition
    ? {
        protein: nutrition.totalProtein,
        hydration: nutrition.totalHydration, // BUG FIX C2: Keep in ml (NOT L!)
      }
    : {
        protein: 0,
        hydration: 0,
      };

  // BUG 20: Memoize formatted values to prevent flicker/recalculation on navigation
  // Only recalculate when underlying data or unit system changes
  // Must be placed before any early returns to follow Rules of Hooks
  const formattedWeight = useMemo(() => {
    return bodyMetrics?.currentWeight
      ? formatBodyWeight(bodyMetrics.currentWeight, bodyUnits)
      : "0";
  }, [bodyMetrics?.currentWeight, bodyUnits]);

  const formattedProtein = useMemo(() => {
    return formatMacro(nutritionData.protein, foodUnits);
  }, [nutritionData.protein, foodUnits]);

  const formattedHydration = useMemo(() => {
    return nutritionData.hydration
      ? formatLiquidVolume(nutritionData.hydration, foodUnits)
      : "0";
  }, [nutritionData.hydration, foodUnits]);

  // C3 — Load baselineWeight once for weight-loss trigger
  useEffect(() => {
    if (!user?.uid || isDemoMode) return;
    getUserProfile(user.uid).then(p => {
      if (p?.baselineWeight) setBaselineWeight(p.baselineWeight);
    }).catch(() => {});
  }, [user?.uid, isDemoMode]);

  // C3 — Upgrade prompt: weight-loss threshold (5kg or 10lb)
  useEffect(() => {
    if (isPremium || previewActive || isDemoMode) return;
    if (!onboardingComplete) return;
    if (!baselineWeight || !bodyMetrics?.currentWeight) return;
    const lossKg = baselineWeight - bodyMetrics.currentWeight;
    const thresholdKg = bodyUnits === 'imperial' ? 4.536 : 5;
    if (lossKg >= thresholdKg) {
      triggerUpgradePrompt('weight_loss');
    }
  }, [isPremium, previewActive, isDemoMode, onboardingComplete, baselineWeight, bodyMetrics?.currentWeight, bodyUnits]);

  useEffect(() => {
    if (!consistencySheetOpen) return;
    if (!user && !isDemoMode) return;
    const uid = isDemoMode ? 'demo-user' : user!.uid;
    getCelebrations(uid)
      .then(records => setEarnedCelebrations(records))
      .catch(() => setEarnedCelebrations([]));
  }, [consistencySheetOpen, user, isDemoMode]);

  // ITEM 4: Demo Mode - allow access without user
  if (!user && !isDemoMode) {
    return (
      <div className="bg-gradient-to-b from-[#f9f3f0] from-[41.312%] to-white">
        <div className="mx-auto max-w-md px-4 pt-20 pb-24">
          <div className="rounded-2xl bg-white/80 border border-gray-200/40 p-4">
            <p className="text-sm text-gray-500">
              You need to be logged in to view this data.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Performance: Render immediately with cached data, don't block on loading
  // Show loading state only if we have no cached data AND are actively loading
  // ITEM 2: Never show empty Home screen - show skeleton during cold start
  // This allows instant render with cached snapshot, then background refresh updates
  // ITEM 4: Demo mode always has data, never show loading
  const hasCachedData = tdee || nutrition || dailyBurn;

  // Show skeleton if:
  // 1. Not in demo mode
  // 2. AND (loading daily summary OR waiting for auth)
  // 3. AND no cached data available
  // This prevents blank screen during cold start (iOS/PWA) when:
  // - JS bundle is loading
  // - Firebase auth is resolving
  // - Daily summary is recomputing
  const shouldShowLoading =
    !isDemoMode && !hasCachedData && (loading || authLoading);

  // ITEM 2: Use skeleton/shimmer instead of spinner for better UX
  // Show skeleton during cold start to prevent blank screen
  if (shouldShowLoading) {
    return <HomeScreenSkeleton />;
  }

  // If TDEE is still null after loading, show an inline setup card.
  // Do NOT use an early return here — the verification banner above must always render.
  if (!tdee) {
    const isNewUser = onboardingComplete && !loading && !authLoading;
    return (
      <div className="bg-gradient-to-b from-[#f9f3f0] from-[41.312%] to-white min-h-screen" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        {/* Email verification banner — rendered first, never skipped */}
        {!emailVerificationCompletedAt && !isAdmin && !isDemoMode && (
          <EmailVerificationBanner
            email={user?.email ?? null}
          />
        )}
        <div className="mx-auto max-w-md px-4 pt-20 pb-24">
          <div className="rounded-2xl bg-white/80 border border-gray-200/40 p-4">
            <p className="text-sm font-medium text-gray-700 mb-1">
              {loading || authLoading
                ? 'Loading your targets…'
                : isNewUser
                  ? 'Your daily targets aren\'t set up yet.'
                  : 'We couldn\'t calculate your targets. Please complete your setup.'}
            </p>
            {!loading && !authLoading && (
              <button
                type="button"
                onClick={() => { navigate('/onboarding'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="mt-2 text-xs font-medium text-[#007c86] underline underline-offset-2"
              >
                Set up your targets →
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Unconfigured TDEE: show CTA instead of silent zeros ──
  // When isConfigured === false, body metrics are incomplete and TDEE can't be calculated.
  // Show an actionable message with the specific missing fields.
  const tdeeUnconfigured = tdee.isConfigured === false;

  // Use the actual TDEE data - it should always be valid at this point
  // CRITICAL: Use tdeeData.caloriesEaten to ensure consistency with TDEE calculation
  const tdeeData = tdee;
  const caloriesEaten = tdeeData.caloriesEaten;

  // Calculate breakdown components for info dropdown
  // CRITICAL FIX: Use tdeeData fields directly for accurate calculations
  // Resting TDEE = base metabolic rate (BMR × 1.2 sedentary factor)
  const resting = tdeeData.baseTdee;

  // BUG FIX #31: Activity Burn = Exercise calories + Residual burn (EPOC)
  // Activity Burn includes ALL logged activity: stepsBurn + runsWalksBurn + weightliftingBurn + manualBurn + EPOC
  // This matches the Exercise screen definition: Activity burn = Exercise + Residual burn
  const activityBurn = (dailyBurn?.totalDailyBurn || 0) + (tdeeData.epoc || 0);

  // BUG FIX #31: Total Burn = Resting + Activity Burn
  // This represents the total calories burned in a day
  // Formula: Total Burn = Resting + (Exercise + Residual burn) = Resting + Exercise + EPOC
  const dailyBurnTotal = resting + activityBurn;

  // BUG FIX #31: Log values to verify Activity Burn consistency between Home and Exercise screens

  // Calculate balance for display: balance = calories_consumed - total_daily_burn
  // When unconfigured, balance is meaningless — UI will show "—" instead
  const balance = tdeeUnconfigured ? 0 : (caloriesEaten - dailyBurnTotal);
  const balanceDirection = tdeeUnconfigured ? 'Neutral' :
    balance < 0 ? "Deficit" : balance > 0 ? "Surplus" : "Neutral";
  const balanceValue = Math.abs(balance);

  // Ensure calories consumed always uses the most up-to-date nutrition data
  const displayCaloriesEaten = caloriesEaten;

  // Safety Status helper functions removed - only shows on GLP-1 Hub per Sprint C spec

  // ── Streak suffix helpers ──────────────────────────────────────────────────
  function getLoggingStreakSuffix(days: number): string | null {
    if (days >= 90) return 'Outstanding discipline';
    if (days >= 60) return 'Excellent consistency';
    if (days >= 30) return 'Habit established';
    if (days >= 14) return 'Routine building';
    if (days >= 7)  return 'Strong consistency';
    if (days >= 3)  return 'Habit starting';
    return null;
  }

  function getDeficitStreakSuffix(days: number): string | null {
    if (days >= 90) return 'Exceptional discipline';
    if (days >= 60) return 'Excellent control';
    if (days >= 30) return 'Consistent progress';
    if (days >= 14) return 'Momentum building';
    if (days >= 7)  return 'Solid discipline';
    if (days >= 3)  return 'Early control';
    return null;
  }

  return (
    <div
      className="bg-gradient-to-b from-[#f9f3f0] from-[41.312%] to-white relative"
      style={{
        paddingTop: "env(safe-area-inset-top)",
      }}
    >
      {/* Email verification banner — always shown for unverified non-admin users, independent of all other state */}
      {!emailVerificationCompletedAt && !isAdmin && !isDemoMode && (
        <EmailVerificationBanner
          email={user?.email ?? null}
        />
      )}

      {/* Messaging — rendered as fixed overlay below, not inline */}

      {/* A2HS nudge — floating, only when coordinator assigns floating slot to it */}
      {activeFloating === 'a2hs' && (
        <div style={{
          position: 'fixed',
          top: 'env(safe-area-inset-top)',
          left: 0, right: 0,
          zIndex: 1002,
          display: 'flex',
          justifyContent: 'center',
          padding: '8px 16px 0',
          pointerEvents: 'auto',
        }}>
          <div style={{
            width: '100%',
            maxWidth: 448,
            background: '#007c86',
            borderRadius: 14,
            padding: '10px 14px 10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}>
            <span style={{ fontSize: 13, color: '#fff', lineHeight: 1.4, fontWeight: 500, flex: 1 }}>
              Add CoreTrak to your home screen for quick access. Check FAQ in Settings for details.
            </span>
            <button
              type="button"
              onClick={dismissA2hsNudge}
              aria-label="Dismiss"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'rgba(255,255,255,0.7)', flexShrink: 0, lineHeight: 1 }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Celebration balloon — floating, only when coordinator assigns floating slot to it */}
      {activeFloating === 'celebration' && celebration && (
        <div style={{
          position: 'fixed',
          top: '30%',
          left: 16, right: 16, zIndex: 1001,
          maxWidth: 448, margin: '0 auto',
          pointerEvents: 'auto',
        }}>
          <Balloon
            tier={celebration.tier}
            message={celebration.message}
            subtext={celebration.subtext}
            onDismiss={dismissCelebration}
          />
        </div>
      )}

      {/* Guidance balloon — floating, only when coordinator assigns floating slot to it */}
      {activeFloating === 'guidance' && guidance && (
        <div style={{
          position: 'fixed',
          top: '30%',
          left: 16, right: 16, zIndex: 1001,
          maxWidth: 448, margin: '0 auto',
          pointerEvents: 'auto',
        }}>
          <Balloon
            tier="bronze"
            message={guidance.message}
            subtext={guidance.subtext}
            onDismiss={dismissGuidance}
          />
        </div>
      )}




      {/* Header */}
      <div
        className="mx-auto max-w-md px-4 pt-0.5 pb-1"
      >
        <div className="flex items-center justify-between">
          {/* Settings button — moved to left */}
          <button
            type="button"
            onClick={() => {
              navigate("/settings");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center justify-center"
            aria-label="Settings"
          >
            <img
              src="/Home/settings.svg"
              alt=""
              className="h-12 w-12"
            />
          </button>

          {/* Title */}
          <h1 className="text-xl font-medium text-[#141517] tracking-[-0.5px]">
            Your Snapshot
          </h1>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><PreviewCountdownPill /></div>
        </div>

        {/* Safety Status removed from Home - only shows on GLP-1 Hub per Sprint C spec */}
      </div>

      {/* StarterModal — modal slot, suppresses all other surfaces while open */}
      {activeModal === 'starter' && (
        <StarterModal isOpen={true} onDismiss={dismissStarterModal} title="Your Daily Snapshot" description="This is your control centre. It shows your real daily balance between food and activity." bullets={['Calories show what you\u2019ve eaten','Daily Burn shows what your body used','Deficit shows progress toward fat loss','Messages guide you if something needs attention']} why="Small daily actions create steady results." />
      )}

      {/* Main Content */}
      <div
        className="mx-auto max-w-md px-4"
      >
        {/* ── First-run state: one card at a time, in priority order ── */}
        {(() => {
          if (isAdmin || isDemoMode) return null;
          if (!onboardingComplete) return null;

          // Priority 1: body metrics incomplete
          if (tdeeUnconfigured) return null; // handled below by the existing unconfigured block

          // Priority 2: unverified + preview not yet used — banner already rendered above, skip upgrade card
          const emailVerified = emailVerificationCompletedAt != null;
          if (!emailVerified && !premiumPreviewUsed && !isPremium && !previewActive) {
            return null;
          }

          // Priority 3: Basic user, verified, metrics complete — show calm upgrade prompt
          if (!isPremium && !previewActive && emailVerified) {
            return (
              <div className="mb-3 rounded-2xl bg-white border border-gray-200/60 p-4">
                <p className="text-sm font-semibold text-gray-800 mb-1">Upgrade to Trak</p>
                <p className="text-xs text-gray-500 mb-3">
                  Unlock insights, trend analysis, label parsing, and advanced coaching features.
                </p>
                <button
                  type="button"
                  onClick={() => { navigate('/settings'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="text-xs font-medium text-[#007c86] underline underline-offset-2"
                >
                  See what's included →
                </button>
              </div>
            );
          }

          return null;
        })()}

        {/* ── Unconfigured TDEE: actionable CTA ── */}
        {tdeeUnconfigured && (
          <div className="mb-3 rounded-2xl bg-amber-50/80 border border-amber-200/50 p-4">
            <p className="text-sm font-medium text-amber-900 mb-1">
              Daily targets not yet set up
            </p>
            <p className="text-xs text-amber-700 mb-2">
              Complete your target setup to enable daily burn tracking
              {tdee.missingFields && tdee.missingFields.length > 0 && (
                <> — missing: {tdee.missingFields.join(', ')}</>
              )}
            </p>
            <button
              type="button"
              onClick={() => { navigate('/onboarding'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="text-xs font-medium text-amber-800 underline underline-offset-2"
            >
              Set up your targets →
            </button>
          </div>
        )}
        {/* Calorie Balance Card */}
        <CalorieBalanceCard
          yesterdayDeficit={yesterdayTDEE ? yesterdayTDEE.deficit : null}
          balanceDirection={balanceDirection}
          balanceValue={balanceValue}
          displayCaloriesEaten={displayCaloriesEaten}
          dailyBurnTotal={dailyBurnTotal}
          resting={resting}
          activityBurn={activityBurn}
          foodUnits={foodUnits}
          weeklyInsight={weeklyInsight}
          daysLogged={consistencyData.loggingCount}
        />

        <div style={{ height: 4 }} />

        {/* Streaks — tappable card, opens consistency sheet */}
        {(() => {
          const days = consistencyData?.days ?? [];
          const dates = consistencyData?.dates ?? [];
          const today = getTodayDate();
          const todayIndex = days.length - 1;

          const logStartIndex = (dates[todayIndex] === today && !days[todayIndex].hasMeals)
            ? todayIndex - 1 : todayIndex;
          let loggingStreak = 0;
          for (let i = logStartIndex; i >= 0; i--) {
            if (days[i].hasMeals) loggingStreak++;
            else break;
          }

          const defStartIndex = dates[todayIndex] === today ? todayIndex - 1 : todayIndex;
          let deficitStreak = 0;
          for (let i = defStartIndex; i >= 0; i--) {
            if (days[i].deficitOnTarget) deficitStreak++;
            else break;
          }

          // Last streak fallback — scan backwards past current gap to find previous streak
          let lastLoggingStreak = 0;
          let lastLoggingStreakEndDate: string | null = null;
          let lastDeficitStreak = 0;
          let lastDeficitStreakEndDate: string | null = null;

          if (loggingStreak === 0 && days.length > 0) {
            // Skip gap days then count last streak
            let i = logStartIndex;
            while (i >= 0 && !days[i].hasMeals) i--;
            if (i >= 0) { lastLoggingStreakEndDate = dates[i] ?? null; }
            while (i >= 0 && days[i].hasMeals) { lastLoggingStreak++; i--; }
          }

          if (deficitStreak === 0 && days.length > 0) {
            let i = defStartIndex;
            while (i >= 0 && !days[i].deficitOnTarget) i--;
            if (i >= 0) { lastDeficitStreakEndDate = dates[i] ?? null; }
            while (i >= 0 && days[i].deficitOnTarget) { lastDeficitStreak++; i--; }
          }

          // Format a date string YYYY-MM-DD as '26 Mar'
          const fmtDate = (d: string | null) => {
            if (!d) return '';
            const dt = new Date(d + 'T12:00:00');
            return dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
          };

          let logColour = '#6B7280';
          let logWeight: number = 500;
          if (loggingStreak >= 30) { logColour = '#B45309'; logWeight = 600; }
          else if (loggingStreak >= 14) { logColour = '#007c86'; logWeight = 600; }
          else if (loggingStreak >= 7) { logColour = '#007c86'; logWeight = 500; }

          const logSuffix = getLoggingStreakSuffix(loggingStreak);
          const defSuffix = getDeficitStreakSuffix(deficitStreak);
          const sheetDays = days.slice(-14);

          const tierConfig: Record<string, { medal: string; bg: string; border: string; titleColor: string; subtextColor: string; label: string; labelColor: string }> = {
            gold:   { medal: '🥇', bg: '#FEF9EE', border: 'rgba(180,83,9,0.15)',   titleColor: '#92400E', subtextColor: '#B45309', label: 'Gold',   labelColor: '#D97706' },
            silver: { medal: '🥈', bg: '#F8FAFC', border: 'rgba(100,116,139,0.15)', titleColor: '#334155', subtextColor: '#64748B', label: 'Silver', labelColor: '#64748B' },
            bronze: { medal: '🥉', bg: '#FDF7F4', border: 'rgba(154,52,18,0.12)',   titleColor: '#7C2D12', subtextColor: '#9A3412', label: 'Bronze', labelColor: '#9A3412' },
          };

          return (
            <>
              <button
                type="button"
                onClick={() => setConsistencySheetOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', background: 'rgba(255,255,255,0.7)',
                  border: '1px solid rgba(0,0,0,0.06)', borderRadius: 16,
                  padding: '10px 14px', marginBottom: 4, cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 15, color: loggingStreak > 0 ? logColour : '#6B7280', fontWeight: loggingStreak > 0 ? logWeight : 500, letterSpacing: -0.2, whiteSpace: 'nowrap' }}>
                      🔥 {loggingStreak > 0 ? `${loggingStreak}-day logging streak${logSuffix ? ` — ${logSuffix}` : ''}` : lastLoggingStreak > 0 ? `Last logging streak: ${lastLoggingStreak} days` : 'No logging streak yet'}
                    </span>
                    {loggingStreak === 0 && lastLoggingStreak > 0 && lastLoggingStreakEndDate && (
                      <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>🏁 {fmtDate(lastLoggingStreakEndDate)}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 15, color: deficitStreak > 0 ? '#007c86' : '#6B7280', fontWeight: 500, letterSpacing: -0.2, whiteSpace: 'nowrap' }}>
                      🎯 {deficitStreak > 0 ? `${deficitStreak}-day deficit streak${defSuffix ? ` — ${defSuffix}` : ''}` : lastDeficitStreak > 0 ? `Last deficit streak: ${lastDeficitStreak} days` : 'No deficit streak yet'}
                    </span>
                    {deficitStreak === 0 && lastDeficitStreak > 0 && lastDeficitStreakEndDate && (
                      <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>🏁 {fmtDate(lastDeficitStreakEndDate)}</span>
                    )}
                  </div>
                </div>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007c86" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginLeft: 10 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </button>

              {consistencySheetOpen && createPortal(
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
                  <div
                    onClick={() => setConsistencySheetOpen(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)' }}
                  />
                  <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 448, background: '#fff', borderRadius: '20px 20px 0 0', maxHeight: '82vh', display: 'flex', flexDirection: 'column', zIndex: 1 }}>
                    {/* Fixed header — drag handle + title + streaks only */}
                    <div style={{ flexShrink: 0, padding: '0 20px' }}>
                      <div style={{ width: 36, height: 4, background: '#E5E7EB', borderRadius: 2, margin: '12px auto 16px' }} />
                      <p style={{ fontSize: 17, fontWeight: 600, color: '#141517', letterSpacing: -0.3, margin: '0 0 5px' }}>Your consistency</p>
                      {loggingStreak > 0 && (
                        <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 2px' }}>
                          🔥 {loggingStreak}-day logging streak{logSuffix ? ` — ${logSuffix}` : ''}
                        </p>
                      )}
                      {deficitStreak > 0 && (
                        <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 0' }}>
                          🎯 {deficitStreak}-day deficit streak{defSuffix ? ` — ${defSuffix}` : ''}
                        </p>
                      )}
                    </div>
                    {/* Scrollable body — everything below the streaks */}
                    <div style={{ overflowY: 'auto', flex: 1, padding: '14px 20px 80px' }}>
                      <p style={{ fontSize: 11, fontWeight: 500, color: '#9CA3AF', letterSpacing: '0.05em', margin: '0 0 8px', textTransform: 'uppercase' }}>Last 14 days</p>
                      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
                        {sheetDays.map((d, i) => {
                          let bg = '#E5E7EB';
                          if (d.hasMeals && d.deficitOnTarget) bg = '#007c86';
                          else if (d.hasMeals) bg = '#9FE1CB';
                          return <div key={i} style={{ width: 18, height: 18, borderRadius: 5, background: bg, flexShrink: 0 }} />;
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: 20, marginBottom: 18 }}>
                        {[
                          { color: '#007c86', label: 'Deficit' },
                          { color: '#9FE1CB', label: 'Logged' },
                          { color: '#E5E7EB', label: 'Missed' },
                        ].map(({ color, label }) => (
                          <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                            <div style={{ width: 24, height: 10, borderRadius: 3, background: color }} />
                            <span style={{ fontSize: 11, color: '#9CA3AF' }}>{label}</span>
                          </div>
                        ))}
                      </div>

                      {earnedCelebrations.length === 0 && (
                        <div style={{ borderTop: '0.5px solid #F3F4F6', paddingTop: 16, marginBottom: 10 }}>
                          <p style={{ fontSize: 11, fontWeight: 500, color: '#9CA3AF', letterSpacing: '0.05em', margin: '0 0 6px', textTransform: 'uppercase' }}>Milestones earned</p>
                          <p style={{ fontSize: 13, color: '#D1D5DB', margin: 0, fontStyle: 'italic' }}>Milestones will appear here</p>
                        </div>
                      )}
                      {earnedCelebrations.length > 0 && (
                        <>
                          {(() => {
                            const grouped = new Map<string, { tier: string; title: string; count: number }>();
                            const tierRank: Record<string, number> = { gold: 0, silver: 1, bronze: 2 };
                            for (const c of earnedCelebrations) {
                              const existing = grouped.get(c.eventKey);
                              if (existing) { existing.count += 1; }
                              else { grouped.set(c.eventKey, { tier: c.tier, title: c.title || c.message, count: 1 }); }
                            }
                            const sorted = Array.from(grouped.values()).sort((a, b) => (tierRank[a.tier] ?? 2) - (tierRank[b.tier] ?? 2));
                            const goldCount = sorted.filter(m => m.tier === 'gold').length;
                            const silverCount = sorted.filter(m => m.tier === 'silver').length;
                            const bronzeCount = sorted.filter(m => m.tier === 'bronze').length;
                            return (
                              <>
                                <div style={{ borderTop: '0.5px solid #F3F4F6', paddingTop: 16, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <p style={{ fontSize: 11, fontWeight: 500, color: '#9CA3AF', letterSpacing: '0.05em', margin: 0, textTransform: 'uppercase' }}>Milestones earned</p>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    {goldCount > 0 && <span style={{ fontSize: 13 }}>🥇<span style={{ fontSize: 11, fontWeight: 600, color: '#D97706', marginLeft: 2 }}>{goldCount}</span></span>}
                                    {silverCount > 0 && <span style={{ fontSize: 13 }}>🥈<span style={{ fontSize: 11, fontWeight: 600, color: '#64748B', marginLeft: 2 }}>{silverCount}</span></span>}
                                    {bronzeCount > 0 && <span style={{ fontSize: 13 }}>🥉<span style={{ fontSize: 11, fontWeight: 600, color: '#9A3412', marginLeft: 2 }}>{bronzeCount}</span></span>}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                  {sorted.map((c) => {
                                    const cfg = tierConfig[c.tier] ?? tierConfig.bronze;
                                    return (
                                      <div key={c.tier + c.title} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: cfg.bg, border: `0.5px solid ${cfg.border}`, borderRadius: 12 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                                          <span style={{ fontSize: 20, lineHeight: 1 }}>{cfg.medal}</span>
                                          {c.count > 1 && <span style={{ fontSize: 11, fontWeight: 600, color: cfg.labelColor }}>×{c.count}</span>}
                                        </div>
                                        <p style={{ fontSize: 13, fontWeight: 500, color: cfg.titleColor, margin: 0, flex: 1, lineHeight: 1.3 }}>{c.title}</p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>
                </div>,
                document.body,
              )}
            </>
          );
        })()}

        {/* Home message — inline, only when no modal or floating surface is active */}
        {activeInline === 'homeMessage' && homeMessage && (
          <div style={{ marginBottom: 8 }}>
            <HomeMessageCard message={homeMessage} onDismiss={dismissHomeMessage} />
          </div>
        )}

        {/* Dashboard Metric Tiles */}
        <HomeDashboardTiles
          formattedWeight={formattedWeight}
          formattedProtein={formattedProtein}
          formattedHydration={formattedHydration}
          hydrationRaw={nutritionData.hydration}
          proteinRaw={nutritionData.protein}
          foodUnits={foodUnits}
          bodyUnits={bodyUnits}
          targets={targets}
          onNavigate={(path) => { navigate(path); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        />

        <div style={{ height: 6 }} />

        {/* CTA Buttons */}
        <div className="grid grid-cols-2 gap-2" data-home-cta-buttons>
          <button
            type="button"
            onClick={() => {
              navigate("/nutrition");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            style={{ height: 48, borderRadius: 24, background: '#007c86', color: '#fff', fontSize: 14, fontWeight: 600, letterSpacing: -0.2, boxShadow: '0px 2px 5px 0px rgba(0,124,134,0.06)', border: 'none', cursor: 'pointer' }}
            className="hover:bg-[#006a73] transition-colors"
          >
            Log Meal
          </button>
          <button
            type="button"
            onClick={() => {
              navigate("/workouts");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            style={{ height: 48, borderRadius: 24, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', color: '#007c86', fontSize: 14, fontWeight: 600, letterSpacing: -0.2, boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.03)', cursor: 'pointer' }}
            className="hover:bg-gray-50 transition-colors"
          >
            Log Exercise
          </button>
        </div>
      </div>

    </div>
  );
}
